import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    CircularProgress,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import {
    mediateLoginInfoDependent,
    mediateSelectedListInfoDependent,
} from 'mediators';

import {
    Suggestion,
    SuggestionEntry,
    applySuggestion,
    dismissSuggestion,
    chatWithAssistant,
    sugFmt,
    getConfLabel,
    CAT_COLORS,
    KIND_ICONS,
    QUICK_REPLIES,
    ChatSession,
    getChatSessions,
    getChatSession,
    deleteChatSession,
} from '../core/services/suggestions.service';

// ── Design tokens ──

const PURPLE = '#7C3AED';
const PURPLE_SOFT = '#EDE9FE';
const INK = '#0F172A';
const INK_700 = '#334155';
const SLATE = '#64748B';
const SLATE_LIGHT = '#94A3B8';
const HAIR = '#E2E8F0';
const GREEN = '#10B981';
const RED = '#EF4444';

// ── SVG Icon helper (inline, no font dependency) ──

const ICON_PATHS: Record<string, string> = {
    add: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
    chevron_left: 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z',
    chevron_right: 'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
    close: 'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
    history: 'M13 3a9 9 0 00-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0013 21a9 9 0 000-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z',
    auto_awesome: 'M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z',
    send: 'M2.01 21L23 12 2.01 3 2 10l15 2-15 2z',
    check: 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
    check_circle: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    edit: 'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
    visibility: 'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z',
    psychology: 'M13 8.57c-.79 0-1.43.64-1.43 1.43s.64 1.43 1.43 1.43 1.43-.64 1.43-1.43-.64-1.43-1.43-1.43zM13 3C9.25 3 6.2 5.94 6.02 9.64L4.1 12.2c-.25.33-.01.8.4.8H6v3c0 1.1.9 2 2 2h1v3h7v-4.68c2.36-1.12 4-3.53 4-6.32 0-3.87-3.13-7-7-7zm3 8c0 .13-.01.27-.02.4l.93.78c.08.07.1.19.05.28l-.88 1.51c-.05.09-.16.13-.26.09l-1.09-.44c-.23.17-.48.31-.75.42l-.17 1.16c-.02.1-.1.18-.21.18h-1.76c-.1 0-.19-.08-.21-.18l-.17-1.16c-.27-.11-.52-.25-.75-.42l-1.09.44c-.1.04-.21 0-.26-.09l-.88-1.51c-.05-.09-.03-.21.05-.28l.93-.78c-.01-.13-.02-.27-.02-.4 0-.13.01-.27.02-.4l-.93-.78c-.08-.07-.1-.19-.05-.28l.88-1.51c.05-.09.16-.13.26-.09l1.09.44c.23-.17.48-.31.75-.42l.17-1.16c.02-.1.1-.18.21-.18h1.76c.1 0 .19.08.21.18l.17 1.16c.27.11.52.25.75.42l1.09-.44c.1-.04.21 0 .26.09l.88 1.51c.05.09.03.21-.05.28l-.93.78c.01.13.02.26.02.4z',
    home: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
    bolt: 'M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z',
    directions_car: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
    movie: 'M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z',
    favorite: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
    restaurant: 'M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z',
    trending_up: 'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    more_horiz: 'M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    category: 'M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z',
    bookmark_add: 'M17 11V8h-2v3h-3v2h3v3h2v-3h3v-2h-3zm2-9H5c-1.1 0-2 .9-2 2v18l7-3 7 3V4c0-1.1-.9-2-2-2z',
    content_copy: 'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
    library_add: 'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z',
    event_repeat: 'M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14a2 2 0 002 2h6v-2H5V10h14v3h2V6c0-1.1-.9-2-2-2zm0 4H5V6h14v2zm.65 7.35L22 13v6h-6l2.61-2.61c-.71-.86-1.79-1.39-2.99-1.39A3.99 3.99 0 0011.86 18l-1.79.79c.32-1.07 1.21-3.16 4-3.65a4.96 4.96 0 015.58 1.21z',
    tune: 'M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z',
    copy_all: 'M21 19V3H8v3H3v15h13v-3h5zM10 17V5h9v12h-9zM5 8h3v9h7v2H5V8z',
};

const SugIcon: React.FC<{ name: string; size?: number; color?: string; style?: React.CSSProperties }> = ({ name, size = 18, color = INK, style }) => {
    const path = ICON_PATHS[name];
    if (!path) return <span style={{ fontSize: size * 0.6, color, ...style }}>•</span>;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'inline-block', flexShrink: 0, ...style }}>
            <path d={path} />
        </svg>
    );
};

// ── Confidence chip ──

const ConfChip: React.FC<{ confidence: number }> = ({ confidence }) => {
    const c = getConfLabel(confidence);
    return (
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 10, color: c.color, background: c.bg, flexShrink: 0, marginTop: 2 }}>
            {c.label} · {Math.round(confidence * 100)}%
        </span>
    );
};

// ── Markdown renderer (using marked) ──

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { marked } = require('marked');

const mdStyles = `
.md-chat { text-align: left; }
.md-chat p { margin: 4px 0; }
.md-chat ul, .md-chat ol { margin: 4px 0; padding-left: 20px; text-align: left; }
.md-chat li { margin: 2px 0; }
.md-chat code { background: #F1F5F9; padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
.md-chat pre { background: #F1F5F9; padding: 10px 12px; border-radius: 6px; overflow-x: auto; margin: 6px 0; }
.md-chat pre code { background: none; padding: 0; }
.md-chat table { width: 100%; border-collapse: collapse; font-size: 13px; margin: 6px 0; }
.md-chat th { padding: 6px 10px; border-bottom: 2px solid #E2E8F0; text-align: left; font-weight: 700; font-size: 12px; color: #64748B; }
.md-chat td { padding: 5px 10px; border-bottom: 1px solid #F1F5F9; }
.md-chat tr:nth-child(even) { background: #F8FAFC; }
.md-chat blockquote { border-left: 3px solid #E2E8F0; margin: 6px 0; padding: 4px 12px; color: #64748B; }
.md-chat a { color: #7C3AED; text-decoration: none; }
.md-chat h1, .md-chat h2, .md-chat h3 { margin: 8px 0 4px; }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('md-chat-styles')) {
    const style = document.createElement('style');
    style.id = 'md-chat-styles';
    style.textContent = mdStyles;
    document.head.appendChild(style);
}

const renderMarkdown = (text: string): React.ReactNode => {
    const html = marked.parse(text, { breaks: true }) as string;
    return <div className="md-chat" dangerouslySetInnerHTML={{ __html: html }} />;
};

// ── Suggestion card in chat ──

interface CardProps {
    sug: Suggestion;
    selected: boolean;
    applied: boolean;
    showConfidence: boolean;
    onSelect: () => void;
    onApply: () => void;
    onDismiss: () => void;
    onEdit: () => void;
}

const SugCard: React.FC<CardProps> = ({ sug, selected, applied, showConfidence, onSelect, onApply, onDismiss, onEdit }) => {
    const entries = Array.isArray(sug.entries) ? sug.entries : [];
    const realEntries = entries.filter(e => e.amount != null);
    return (
        <div style={{
            maxWidth: 560, background: '#fff', border: `1px solid ${HAIR}`, borderLeft: `3px solid ${PURPLE}`,
            borderRadius: '4px 12px 12px 12px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
            ...(selected ? { borderColor: PURPLE, boxShadow: '0 0 0 3px rgba(124,58,237,0.12)' } : {}),
        }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: PURPLE_SOFT, color: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <SugIcon name={KIND_ICONS[sug.kind]} size={20} color={PURPLE} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25 }}>{sug.title}</div>
                    <div style={{ fontSize: 13, color: INK_700, lineHeight: 1.5, marginTop: 4 }}>{sug.summary}</div>
                </div>
                {showConfidence && <ConfChip confidence={sug.confidence} />}
            </div>

            <div style={{ display: 'flex', gap: 18, fontSize: 12, color: SLATE, padding: '2px 0 4px' }}>
                {sug.total != null && <span><span style={{ color: INK, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{sugFmt(sug.total)}</span> total</span>}
                <span><span style={{ color: INK, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{realEntries.length}</span> entries</span>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {applied ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: GREEN, padding: '8px 12px', background: '#D1FAE5', borderRadius: 8 }}>
                        <SugIcon name="check_circle" size={14} color={GREEN} />
                        Applied
                    </span>
                ) : (
                    <>
                        <div onClick={onApply} style={{ height: 36, padding: '0 14px', borderRadius: 8, background: PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            <SugIcon name="check" size={14} color="#fff" />
                            {sug.primaryLabel}
                        </div>
                        <div onClick={onSelect} style={{ height: 36, padding: '0 12px', borderRadius: 8, background: '#F1F5F9', color: INK_700, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            <SugIcon name="visibility" size={14} color={INK_700} />
                            Preview
                        </div>
                        <div onClick={onEdit} style={{ fontSize: 13, fontWeight: 600, color: PURPLE, padding: '0 6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>Edit</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: PURPLE, padding: '0 6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>Why this?</div>
                        <div onClick={onDismiss} style={{ fontSize: 13, fontWeight: 600, color: SLATE, padding: '0 6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>Dismiss</div>
                    </>
                )}
            </div>
        </div>
    );
};

// ── Entry row in preview pane ──

const EntryRow: React.FC<{ entry: SuggestionEntry }> = ({ entry }) => {
    const cat = CAT_COLORS[entry.cat] || CAT_COLORS.other;
    const isMore = entry.amount == null;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px', borderRadius: 8, border: `1px solid ${HAIR}`, background: '#fff' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: isMore ? '#F1F5F9' : cat.bg }}>
                <SugIcon name={isMore ? 'more_horiz' : cat.icon} size={14} color={isMore ? SLATE : cat.fg} />
            </div>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, minWidth: 0 }}>{entry.title}</div>
            <div style={{ fontSize: 11, color: SLATE }}>{entry.date}</div>
            {entry.amount != null && (
                <div style={{ fontSize: 13, fontWeight: 700, color: RED, fontVariantNumeric: 'tabular-nums', minWidth: 80, textAlign: 'right' as const }}>
                    {sugFmt(entry.amount)}
                </div>
            )}
        </div>
    );
};

// ── Preview pane (right) ──

const PreviewPane: React.FC<{
    sug: Suggestion | null;
    applied: boolean;
    onApply: () => void;
    onEdit: () => void;
    onDismiss: () => void;
    onPrev: () => void;
    onNext: () => void;
    currentIndex: number;
    totalCount: number;
}> = ({ sug, applied, onApply, onEdit, onDismiss, onPrev, onNext, currentIndex, totalCount }) => {
    if (!sug) {
        return (
            <>
                <div style={{ padding: '14px 18px', borderBottom: `1px solid ${HAIR}`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <SugIcon name="visibility" size={18} color={PURPLE} />
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>Preview</div>
                        <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>Pick a suggestion to see what it changes</div>
                    </div>
                </div>
                <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' as const, color: SLATE }}>
                    <SugIcon name="auto_awesome" size={32} color={SLATE_LIGHT} />
                    <div style={{ fontSize: 13, marginTop: 10, maxWidth: 240 }}>
                        Click <b>Preview</b> on any suggestion to inspect affected entries before applying.
                    </div>
                </div>
            </>
        );
    }

    const allEntries = Array.isArray(sug.entries) ? sug.entries : [];
    const realEntries = allEntries.filter(e => e.amount != null);
    return (
        <>
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${HAIR}`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SugIcon name={KIND_ICONS[sug.kind]} size={18} color={PURPLE} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{sug.title}</div>
                    <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>
                        {Math.round(sug.confidence * 100)}% confident · {realEntries.length} {realEntries.length === 1 ? 'entry' : 'entries'}
                    </div>
                </div>
                {totalCount > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div onClick={onPrev} style={{ width: 28, height: 28, borderRadius: 6, background: currentIndex > 0 ? '#F1F5F9' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentIndex > 0 ? 'pointer' : 'default', opacity: currentIndex > 0 ? 1 : 0.3 }}>
                            <SugIcon name="chevron_left" size={16} color={INK_700} />
                        </div>
                        <span style={{ fontSize: 11, color: SLATE, fontWeight: 600, minWidth: 30, textAlign: 'center' as const }}>{currentIndex + 1}/{totalCount}</span>
                        <div onClick={onNext} style={{ width: 28, height: 28, borderRadius: 6, background: currentIndex < totalCount - 1 ? '#F1F5F9' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentIndex < totalCount - 1 ? 'pointer' : 'default', opacity: currentIndex < totalCount - 1 ? 1 : 0.3 }}>
                            <SugIcon name="chevron_right" size={16} color={INK_700} />
                        </div>
                    </div>
                )}
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {sug.total != null && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 10, border: `1px solid ${HAIR}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: SLATE_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>Total impact</div>
                            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: RED }}>{sugFmt(sug.total)}</div>
                        </div>
                        <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 10, border: `1px solid ${HAIR}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: SLATE_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>Entries</div>
                            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{realEntries.length}</div>
                        </div>
                    </div>
                )}

                <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: SLATE_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 8 }}>Affected entries</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {allEntries.map((e, i) => <EntryRow key={i} entry={e} />)}
                    </div>
                </div>

                <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: SLATE_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: 8 }}>Why Uplift suggested this</div>
                    <div style={{ padding: '12px 14px', background: '#FAF7FF', border: `1px solid ${PURPLE_SOFT}`, borderRadius: 10, fontSize: 12, color: INK_700, lineHeight: 1.55, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <SugIcon name="psychology" size={16} color={PURPLE} />
                        <span>{sug.reasoning}</span>
                    </div>
                </div>
            </div>

            <div style={{ padding: '12px 18px', borderTop: `1px solid ${HAIR}`, display: 'flex', gap: 8, flexShrink: 0 }}>
                {applied ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: GREEN, padding: '8px 12px', background: '#D1FAE5', borderRadius: 8 }}>
                        <SugIcon name="check_circle" size={14} color={GREEN} />
                        Applied
                    </span>
                ) : (
                    <>
                        <div onClick={onApply} style={{ flex: 1, height: 36, borderRadius: 8, background: PURPLE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                            <SugIcon name="check" size={14} color="#fff" />
                            {sug.primaryLabel}
                        </div>
                        <div onClick={onEdit} style={{ height: 36, padding: '0 12px', borderRadius: 8, background: '#F1F5F9', color: INK_700, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            <SugIcon name="edit" size={14} color={INK_700} />
                            Edit
                        </div>
                        <div onClick={onDismiss} style={{ height: 36, padding: '0 12px', borderRadius: 8, color: SLATE, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>
                            <SugIcon name="close" size={14} color={SLATE} />
                            Dismiss
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

// ── Normalize backend suggestion to UI shape ──

const normalizeSuggestion = (raw: any): Suggestion => {
    // Handle data.entries (array) or data.entry (single)
    const dataEntries = Array.isArray(raw.data?.entries) ? raw.data.entries : [];
    const singleEntry = raw.data?.entry;
    const entriesFromData: SuggestionEntry[] = dataEntries.length > 0
        ? dataEntries.map((e: any) => ({
            title: String(e.title || ''),
            amount: e.amount != null ? Number(e.amount) * (e.type === 'DEBIT' ? -1 : 1) : null,
            date: String(e.date || ''),
            cat: (String(e.category || 'other')) as any,
        }))
        : singleEntry ? [{
            title: String(singleEntry.title || ''),
            amount: singleEntry.amount != null ? Number(singleEntry.amount) * (singleEntry.type === 'DEBIT' ? -1 : 1) : null,
            date: String(singleEntry.date || ''),
            cat: (String(singleEntry.category || 'other')) as any,
        }] : [];

    const totalFromData = raw.data?.total != null ? Number(raw.data.total) : null;

    return {
        id: String(raw.id || ''),
        kind: String(raw.kind || raw.data?.kind || 'create_entry') as any,
        title: String(raw.title || ''),
        summary: String(raw.summary || ''),
        primaryLabel: String(raw.primaryLabel || raw.data?.primaryLabel || 'Apply'),
        confidence: Number(raw.confidence || raw.data?.confidence || 0.9),
        total: totalFromData ?? (entriesFromData.length === 1 ? Number(entriesFromData[0].amount) : null),
        reasoning: String(raw.data?.reasoning || raw.reasoning || raw.summary || ''),
        entries: Array.isArray(raw.entries) ? raw.entries : entriesFromData,
    };
};

// ── Main page ──

export const SuggestionsPage: React.FC = () => {
    const login: any = useSelector(mediateLoginInfoDependent);
    const selectedList: any = useSelector(mediateSelectedListInfoDependent);
    const userId = login?.user?.id || '';
    const listId = selectedList?.id || '';

    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

    const [selected, setSelected] = useState<Suggestion | null>(null);
    const [applied, setApplied] = useState<Record<string, boolean>>({});
    const [dismissed, setDismissed] = useState<Record<string, boolean>>({});
    const [composerText, setComposerText] = useState('');
    const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'bot'; content: string }>>([]);
    const [sending, setSending] = useState(false);
    const [sessionId, setSessionIdRaw] = useState<string | null>(() => sessionStorage.getItem('sug_sessionId'));
    const setSessionId = useCallback((id: string | null) => {
        setSessionIdRaw(id);
        if (id) sessionStorage.setItem('sug_sessionId', id);
        else sessionStorage.removeItem('sug_sessionId');
    }, []);
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [sessionsOpen, setSessionsOpen] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [editSuggestion, setEditSuggestion] = useState<Suggestion | null>(null);
    const chatScrollRef = useRef<HTMLDivElement>(null);

    const loadSessions = useCallback(async () => {
        if (!userId) return;
        setSessionsLoading(true);
        try {
            const res = await getChatSessions(userId);
            setSessions(res.sessions || []);
        } catch { /* silently fail */ }
        finally { setSessionsLoading(false); }
    }, [userId]);

    const resumeSession = useCallback(async (sid: string) => {
        if (!userId) return;
        try {
            const res = await getChatSession(sid, userId);
            const s = res.session;
            setSessionId(s.id);
            setChatMessages((s.messages || []).map(m => ({
                role: m.role === 'assistant' ? 'bot' as const : 'user' as const,
                content: m.content,
            })));
            // Load pending suggestions from session
            const pending = (s as any).pendingSuggestions || [];
            const normalized = pending.map(normalizeSuggestion);
            setSuggestions(normalized);
            setApplied({});
            setDismissed({});
            if (normalized.length > 0) setSelected(normalized[0]);
            setSessionsOpen(false);
        } catch { /* silently fail */ }
    }, [userId, setSessionId]);

    const startNewChat = useCallback(() => {
        setSessionId(null);
        setChatMessages([]);
        setSuggestions([]);
        setApplied({});
        setDismissed({});
        setSessionsOpen(false);
    }, [setSessionId]);

    const handleDeleteSession = useCallback(async (sid: string) => {
        try {
            await deleteChatSession(sid);
            setSessions(prev => prev.filter(s => s.id !== sid));
            if (sessionId === sid) startNewChat();
        } catch { /* silently fail */ }
    }, [sessionId, startNewChat]);

    // Auto-resume persisted session on mount
    useEffect(() => {
        const stored = sessionStorage.getItem('sug_sessionId');
        if (stored && userId && chatMessages.length === 0) {
            resumeSession(stored);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const handleApply = async (sug: Suggestion) => {
        setApplied(a => ({ ...a, [sug.id]: true }));
        setSelected(sug);
        try {
            // Backend handles all kinds via the apply endpoint
            if (!sessionId) throw new Error('No active session');
            await applySuggestion(sessionId, sug.id, userId, listId);
        } catch {
            setApplied(a => { const next = { ...a }; delete next[sug.id]; return next; });
        }
    };

    const handleDismiss = async (sug: Suggestion) => {
        setDismissed(d => ({ ...d, [sug.id]: true }));
        if (selected?.id === sug.id) setSelected(null);
        try {
            if (sessionId) await dismissSuggestion(sessionId, sug.id, userId);
        } catch {
            setDismissed(d => { const next = { ...d }; delete next[sug.id]; return next; });
        }
    };

    const botReplyRef = useRef('');

    const handleSend = async () => {
        const text = composerText.trim();
        if (!text || sending) return;
        setComposerText('');
        setChatMessages(prev => [...prev, { role: 'user', content: text }]);
        setSending(true);
        botReplyRef.current = '';
        let botAdded = false;
        try {
            const now = new Date();
            const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            await chatWithAssistant(userId, text, [], month, (event) => {
                if (event.type === 'session' && event.data.sessionId) {
                    setSessionId(event.data.sessionId);
                    return;
                }
                if (event.type === 'text') {
                    botReplyRef.current += String(event.data.content || '');
                    if (!botAdded) {
                        botAdded = true;
                        setChatMessages(prev => [...prev, { role: 'bot', content: botReplyRef.current }]);
                    } else {
                        setChatMessages(prev => {
                            const next = [...prev];
                            next[next.length - 1] = { role: 'bot', content: botReplyRef.current };
                            return next;
                        });
                    }
                } else if (event.type === 'suggestion') {
                    const sug = normalizeSuggestion(event.data);
                    setSuggestions(prev => {
                        const existing = prev.findIndex(s => s.id === sug.id);
                        if (existing >= 0) {
                            const next = [...prev];
                            next[existing] = sug;
                            return next;
                        }
                        return [...prev, sug];
                    });
                    setSelected(prev => {
                        if (!prev) return sug;
                        if (prev.id === sug.id) return sug;
                        return prev;
                    });
                } else if (event.type === 'error') {
                    const errMsg = String(event.data.message || 'Something went wrong.');
                    if (botAdded) {
                        setChatMessages(prev => {
                            const next = [...prev];
                            next[next.length - 1] = { role: 'bot', content: errMsg };
                            return next;
                        });
                    } else {
                        setChatMessages(prev => [...prev, { role: 'bot', content: errMsg }]);
                    }
                }
            }, sessionId || undefined);
        } catch {
            if (!botAdded) {
                setChatMessages(prev => [...prev, { role: 'bot', content: 'Sorry, something went wrong. Try again.' }]);
            }
        } finally {
            setSending(false);
        }
    };

    // Auto-scroll chat to bottom on new messages
    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [chatMessages, suggestions]);

    const visibleSuggestions = suggestions.filter(s => !dismissed[s.id]);

    return (
        <div style={{ width: '100%', height: '100%', minHeight: 0, background: '#F8FAFC', display: 'flex', fontFamily: 'var(--font-sans)', color: INK, overflow: 'hidden' }}>
            {/* Left pane — chat */}
            <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${HAIR}`, minWidth: 0, position: 'relative' }}>
                {/* Chat header */}
                <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${HAIR}`, background: '#fff', flexShrink: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: PURPLE_SOFT, color: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SugIcon name="auto_awesome" size={20} color={PURPLE} />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>Uplift assistant</div>
                        <div style={{ fontSize: 12, color: SLATE, marginTop: 1 }}>Templates, duplicates, and tips from your activity</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                        <div
                            onClick={startNewChat}
                            style={{ height: 32, padding: '0 10px', borderRadius: 8, background: '#F1F5F9', color: INK_700, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                            <SugIcon name="add" size={14} color={INK_700} />
                            New
                        </div>
                        <div
                            onClick={() => { setSessionsOpen(o => !o); if (!sessionsOpen) loadSessions(); }}
                            style={{ height: 32, padding: '0 10px', borderRadius: 8, background: sessionsOpen ? PURPLE_SOFT : '#F1F5F9', color: sessionsOpen ? PURPLE : INK_700, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                            <SugIcon name="history" size={14} color={sessionsOpen ? PURPLE : INK_700} />
                            History
                        </div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: GREEN, padding: '4px 10px', borderRadius: 12, background: '#D1FAE5' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN }} />
                            Online
                        </span>
                    </div>
                </div>

                {/* Sessions sidebar */}
                {sessionsOpen && (
                    <div style={{ position: 'absolute', top: 56, left: 0, right: 380, bottom: 0, zIndex: 20, background: 'rgba(15,23,42,0.3)', display: 'flex' }} onClick={() => setSessionsOpen(false)}>
                        <div style={{ width: 300, background: '#fff', borderRight: `1px solid ${HAIR}`, display: 'flex', flexDirection: 'column', height: '100%' }} onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '14px 16px', borderBottom: `1px solid ${HAIR}`, fontSize: 14, fontWeight: 700 }}>
                                Chat history
                            </div>
                            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
                                {sessionsLoading && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                                        <CircularProgress size={20} style={{ color: PURPLE }} />
                                    </div>
                                )}
                                {!sessionsLoading && sessions.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: 20, color: SLATE, fontSize: 13 }}>No past sessions.</div>
                                )}
                                {sessions.map(s => {
                                    const title = s.title || 'Untitled chat';
                                    const date = s.updatedAt ? new Date(s.updatedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '';
                                    const msgCount = s.messageCount || 0;
                                    const isActive = sessionId === s.id;
                                    return (
                                        <div
                                            key={s.id}
                                            onClick={() => resumeSession(s.id)}
                                            style={{
                                                padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                                                background: isActive ? PURPLE_SOFT : '#fff',
                                                border: isActive ? `1px solid ${PURPLE}` : `1px solid transparent`,
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                                                    <div style={{ fontSize: 11, color: SLATE, marginTop: 2 }}>{date} · {msgCount} messages</div>
                                                </div>
                                                <div
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.id); }}
                                                    style={{ width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: SLATE }}
                                                    title="Delete"
                                                >
                                                    <SugIcon name="close" size={14} color={SLATE} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Chat scroll area */}
                <div ref={chatScrollRef} style={{ flex: 1, overflow: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <>
                        {/* Bot greeting */}
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: PURPLE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <SugIcon name="auto_awesome" size={16} color="#fff" />
                            </div>
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <div style={{ fontSize: 11, color: SLATE, fontWeight: 600 }}>Uplift</div>
                                <div style={{ background: '#fff', border: `1px solid ${HAIR}`, borderRadius: '4px 12px 12px 12px', padding: '12px 14px', fontSize: 14, color: INK_700, lineHeight: 1.55, maxWidth: 540, textAlign: 'left' as const }}>
                                    Hi {login?.user?.displayName?.split(' ')[0] || 'there'} — I can spot recurring entries, suggest templates, or help you copy entries forward. Ask me anything.
                                </div>
                            </div>
                        </div>

                            {/* Suggestion cards */}
                            {visibleSuggestions.map(sug => (
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }} key={sug.id}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: PURPLE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <SugIcon name="auto_awesome" size={16} color="#fff" />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <SugCard
                                            sug={sug}
                                            selected={selected?.id === sug.id}
                                            applied={!!applied[sug.id]}
                                            showConfidence={true}
                                            onSelect={() => setSelected(sug)}
                                            onApply={() => handleApply(sug)}
                                            onDismiss={() => handleDismiss(sug)}
                                            onEdit={() => setEditSuggestion(sug)}
                                        />
                                    </div>
                                </div>
                            ))}

                            {/* Chat messages */}
                            {chatMessages.map((msg, i) => (
                                msg.role === 'user' ? (
                                    <div key={`chat-${i}`} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <div style={{ maxWidth: 520, background: PURPLE, color: '#fff', borderRadius: '12px 12px 4px 12px', padding: '10px 14px', fontSize: 14, lineHeight: 1.5 }}>
                                            {msg.content}
                                        </div>
                                    </div>
                                ) : (
                                    <div key={`chat-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: PURPLE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <SugIcon name="auto_awesome" size={16} color="#fff" />
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <div style={{ fontSize: 11, color: SLATE, fontWeight: 600 }}>Uplift</div>
                                            <div style={{ background: '#fff', border: `1px solid ${HAIR}`, borderRadius: '4px 12px 12px 12px', padding: '12px 14px', fontSize: 14, color: INK_700, lineHeight: 1.55, maxWidth: 540, textAlign: 'left' as const }}>
                                                {renderMarkdown(msg.content)}
                                            </div>
                                        </div>
                                    </div>
                                )
                            ))}

                            {sending && !botReplyRef.current && (
                                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: PURPLE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <SugIcon name="auto_awesome" size={16} color="#fff" />
                                    </div>
                                    <div style={{ background: '#fff', border: `1px solid ${HAIR}`, borderRadius: '4px 12px 12px 12px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <CircularProgress size={14} style={{ color: PURPLE }} />
                                        <span style={{ fontSize: 13, color: SLATE }}>Thinking…</span>
                                    </div>
                                </div>
                            )}

                            {visibleSuggestions.length === 0 && chatMessages.length === 0 && (
                                <Box style={{ textAlign: 'center', padding: 40, color: SLATE }}>
                                    <SugIcon name="auto_awesome" size={32} color={SLATE_LIGHT} />
                                    <div style={{ fontSize: 13, marginTop: 10 }}>No suggestions right now. Add a few entries this month and check back.</div>
                                </Box>
                            )}
                    </>
                </div>

                {/* Quick-reply chips */}
                <div style={{ display: 'flex', gap: 8, padding: '0 20px 8px', overflowX: 'auto', flexShrink: 0 }}>
                    {QUICK_REPLIES.map((q, i) => (
                        <div key={i} onClick={() => setComposerText(q)} style={{ fontSize: 13, padding: '8px 14px', borderRadius: 18, background: '#fff', border: `1px solid ${HAIR}`, color: INK_700, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {q}
                        </div>
                    ))}
                </div>

                {/* Composer */}
                <div style={{ padding: '12px 20px 16px', display: 'flex', alignItems: 'center', gap: 10, borderTop: `1px solid ${HAIR}`, background: '#fff', flexShrink: 0 }}>
                    <input
                        value={composerText}
                        onChange={(e) => setComposerText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder='Ask Uplift… (try "which entries repeat every Friday?")'
                        disabled={sending}
                        style={{ flex: 1, height: 44, borderRadius: 10, padding: '0 16px', background: '#F8FAFC', border: `1px solid ${HAIR}`, fontSize: 14, color: INK, outline: 'none' }}
                    />
                    <div onClick={handleSend} style={{ width: 44, height: 44, borderRadius: 10, background: sending ? SLATE_LIGHT : PURPLE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: sending ? 'default' : 'pointer', flexShrink: 0 }}>
                        <SugIcon name="send" size={18} color="#fff" />
                    </div>
                </div>
            </div>

            {/* Right pane — preview */}
            <div style={{ width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
                <PreviewPane
                    sug={selected}
                    applied={selected ? !!applied[selected.id] : false}
                    onApply={() => { if (selected) handleApply(selected); }}
                    onEdit={() => { if (selected) setEditSuggestion(selected); }}
                    onDismiss={() => { if (selected) handleDismiss(selected); }}
                    currentIndex={selected ? visibleSuggestions.findIndex(s => s.id === selected.id) : -1}
                    totalCount={visibleSuggestions.length}
                    onPrev={() => {
                        const idx = selected ? visibleSuggestions.findIndex(s => s.id === selected.id) : -1;
                        if (idx > 0) setSelected(visibleSuggestions[idx - 1]);
                    }}
                    onNext={() => {
                        const idx = selected ? visibleSuggestions.findIndex(s => s.id === selected.id) : -1;
                        if (idx >= 0 && idx < visibleSuggestions.length - 1) setSelected(visibleSuggestions[idx + 1]);
                    }}
                />
            </div>

            {/* Edit entry dialog — prefilled from suggestion's first entry */}
            {editSuggestion && (() => {
                const sugEntries = Array.isArray(editSuggestion.entries) ? editSuggestion.entries : [];
                return (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30 }} onClick={() => setEditSuggestion(null)}>
                        <div style={{ width: '100%', maxWidth: 600, maxHeight: '80vh', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.20)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${HAIR}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 16, fontWeight: 700 }}>Edit — {editSuggestion.title}</div>
                                    <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>{sugEntries.length} {sugEntries.length === 1 ? 'entry' : 'entries'}</div>
                                </div>
                                <div onClick={() => setEditSuggestion(null)} style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <SugIcon name="close" size={18} color={SLATE} />
                                </div>
                            </div>
                            <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 80px', gap: 8, fontSize: 10, fontWeight: 700, color: SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' as const, padding: '0 0 8px' }}>
                                    <div>Title</div>
                                    <div>Amount</div>
                                    <div>Date</div>
                                    <div>Category</div>
                                </div>
                                {sugEntries.map((entry, i) => {
                                    const amt = entry.amount != null ? String(Math.abs(Number(entry.amount))) : '';
                                    const cat = String(entry.cat || 'other');
                                    const date = String(entry.date || '');
                                    const title = String(entry.title || '');
                                    return (
                                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 80px', gap: 8, marginBottom: 6 }}>
                                            <input
                                                defaultValue={title}
                                                onChange={e => { sugEntries[i] = { ...sugEntries[i], title: e.target.value }; }}
                                                style={{ height: 36, border: `1px solid ${HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, fontWeight: 600, color: INK, outline: 'none' }}
                                            />
                                            <input
                                                defaultValue={amt}
                                                onChange={e => { sugEntries[i] = { ...sugEntries[i], amount: Number(e.target.value) || 0 }; }}
                                                style={{ height: 36, border: `1px solid ${HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, fontWeight: 700, color: INK, outline: 'none', textAlign: 'right' as const }}
                                            />
                                            <input
                                                defaultValue={date}
                                                onChange={e => { sugEntries[i] = { ...sugEntries[i], date: e.target.value }; }}
                                                style={{ height: 36, border: `1px solid ${HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, color: INK_700, outline: 'none' }}
                                            />
                                            <input
                                                defaultValue={cat}
                                                onChange={e => { sugEntries[i] = { ...sugEntries[i], cat: e.target.value as any }; }}
                                                style={{ height: 36, border: `1px solid ${HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, color: INK_700, outline: 'none' }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ padding: '14px 24px', borderTop: `1px solid ${HAIR}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <div onClick={() => setEditSuggestion(null)} style={{ height: 36, padding: '0 14px', borderRadius: 8, background: '#F1F5F9', color: INK_700, display: 'inline-flex', alignItems: 'center', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</div>
                                <div onClick={() => {
                                    setSuggestions(prev => prev.map(s => s.id === editSuggestion.id ? { ...editSuggestion, entries: [...sugEntries] } : s));
                                    setSelected(prev => prev?.id === editSuggestion.id ? { ...editSuggestion, entries: [...sugEntries] } : prev);
                                    setEditSuggestion(null);
                                }} style={{ height: 36, padding: '0 14px', borderRadius: 8, background: PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                    <SugIcon name="check" size={14} color="#fff" />
                                    Save changes
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};
